import { Link, Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Route-level staff gate. UX ONLY — the server enforces staff on every
 * /api/admin/* request (returns 403 forbidden for non-staff regardless of what
 * this renders). This just avoids showing a screen that would 403 anyway.
 *
 * Generic (not coupled to any admin section), so Phase 3 (Activity) reuses it.
 */
export default function RequireStaff({ children }: { children: ReactNode }) {
  const { user, loading, isStaff, profileLoading } = useAuth();
  const location = useLocation();

  // Spinner only while the answer is genuinely unknown.
  if (loading || (user && (profileLoading || isStaff === null))) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper-50 text-ink-500">
        <span>Loading…</span>
      </div>
    );
  }

  // Not signed in → bounce to home (where the sign-in panel lives).
  if (!user) {
    return <Navigate to="/" replace state={{ from: location.pathname }} />;
  }

  // Signed in but not staff → clean, honest panel (no silent redirect).
  if (isStaff !== true) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-paper-50 px-6 text-center text-ink-600">
        <h1 className="text-lg font-semibold text-ink-800">Not authorized</h1>
        <p className="max-w-md text-sm text-ink-500">
          This area is for Henoyo staff only. If you believe you should have access,
          contact your Henoyo contact.
        </p>
        <Link to="/" className="text-sm font-medium text-verdant-600 underline">
          Back to documentation
        </Link>
      </div>
    );
  }

  return <>{children}</>;
}
